import { Autocomplete, AutocompleteProps, Box, debounce, TextField } from '@mui/material';
import { apiGet, apiPost, GoogleLocationInfo, LocationAttrs, locationAttrToItem, LocationItem, locationSelector, locationSlice } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { appStore, RootState } from '../../store';
import { LocationLoader } from '../AppItems';
import { LocationIcon } from './LocationIcon';

export const LocationTextfield = (props: {
    autocompleteProps?: Partial<AutocompleteProps<LocationItem, false, boolean, false>>;
    value: ModelId | null;
    onChange?: (locationId: ModelId) => void;
    onSetEmpty?: () => void;
}) => {
    const location = useSelector((state: RootState) => props.value
        ? locationSelector.selectById(state, props.value)
        : undefined);
    const selectedOption = location ? locationAttrToItem(location) : null;
    const [inputValue, setInputValue] = React.useState('');
    const [searchResults, setSearchResults] = React.useState<LocationItem[]>([]);
    const [loading, setLoading] = React.useState(false);
    const searchSequence = React.useRef(0);

    const searchLocations = React.useMemo(() => debounce(async (query: string, sequence: number) => {
        setLoading(true);
        try {
            const response = await apiGet('locationFind', undefined, { query });
            if (sequence === searchSequence.current) {
                setSearchResults(response);
            }
        } finally {
            if (sequence === searchSequence.current) {
                setLoading(false);
            }
        }
    }, 200), []);

    React.useEffect(() => () => {
        searchLocations.clear();
    }, [searchLocations]);

    React.useEffect(() => {
        if (location) {
            setInputValue(location.name);
        } else if (!props.value) {
            setInputValue('');
        }
    }, [props.value, location]);

    const options = React.useMemo(() => {
        if (!selectedOption) return searchResults;
        return [selectedOption, ...searchResults.filter(option => !(
            option.type === selectedOption.type
            && String(option.value) === String(selectedOption.value)
        ))];
    }, [searchResults, selectedOption]);

    const registerGoogleLocation = async (googleLocation: GoogleLocationInfo) => {
        setLoading(true);
        try {
            const registeredLocation = await apiPost('locationRegister', googleLocation);
            appStore.dispatch(locationSlice.actions.upsertOne(registeredLocation));
            setInputValue(registeredLocation.name);
            setSearchResults([]);
            props.onChange?.(registeredLocation.id);
        } finally {
            setLoading(false);
        }
    };

    const onSelected = (_event: React.SyntheticEvent, option: LocationItem | null) => {
        if (!option) {
            if (props.onSetEmpty) {
                searchSequence.current += 1;
                searchLocations.clear();
                setInputValue('');
                setSearchResults([]);
                props.onSetEmpty();
            }
            return;
        }

        searchSequence.current += 1;
        searchLocations.clear();

        if (option.type === 'googleItem') {
            void registerGoogleLocation(option.data as GoogleLocationInfo);
            return;
        }

        if (option.type === 'locationItem' && option.data) {
            const selectedLocation = option.data as LocationAttrs;
            appStore.dispatch(locationSlice.actions.upsertOne(selectedLocation));
            setInputValue(option.label);
            setSearchResults([]);
            props.onChange?.(selectedLocation.id);
        }
    };

    return <>
        <LocationLoader id={props.value} />
        <Autocomplete<LocationItem, false, boolean, false>
            fullWidth
            value={selectedOption}
            inputValue={inputValue}
            options={options}
            loading={loading}
            disableClearable={!props.onSetEmpty}
            filterOptions={availableOptions => availableOptions}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, currentValue) => option.type === currentValue.type
                && String(option.value) === String(currentValue.value)}
            noOptionsText={inputValue.length > 3 ? 'Aucun emplacement trouvé' : 'Saisissez au moins 4 caractères'}
            loadingText="Recherche en cours…"
            onChange={onSelected}
            onInputChange={(_event, newInputValue, reason) => {
                setInputValue(newInputValue);
                if (reason !== 'input') return;

                searchSequence.current += 1;
                searchLocations.clear();
                if (newInputValue.length > 3) {
                    searchLocations(newInputValue, searchSequence.current);
                } else {
                    setLoading(false);
                    setSearchResults([]);
                }
            }}
            renderOption={(optionProps, option) => <Box
                component="li"
                {...optionProps}
                key={`${option.type}-${option.value}`}
                sx={{ display: 'flex', gap: 1 }}
            >
                <LocationIcon location={option} />
                {option.label}
            </Box>}
            renderInput={params => <TextField
                {...params}
                size="small"
                placeholder="Rechercher une adresse…"
                label="Emplacement / adresse"
            />}
        />
    </>;
};
